// §S679 — PublishControls: the share-link on/off control.
//
// A REAL two-way control backed by the server's share state:
//   • reads truth from GET /v1/creator/apps/:appId (isPublic / slug / ownerHandle)
//   • on  = POST /v1/apps/:appId/share { deviceId, public: true }
//   • off = POST /v1/apps/:appId/share { deviceId, public: false }
//
// When on it shows the CANONICAL share identity — the /r/<owner>/<slug>
// deep link (the only URL we want travelling the world) — with a copy button.
// When off it says honestly what that means, and offers to turn it on. There is
// no community feed to publish to since D73: the flag only decides whether the
// short link resolves.
import { useEffect, useRef, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { Globe, Lock, Check, Copy, RotateCw } from 'lucide-preact';
import { getDeviceId } from '../lib/deviceId';
import { canonicalShareUrl } from '../lib/share-urls';
import { t } from '../lib/i18n';

interface ShareState {
  isPublic: boolean;
  slug: string | null;
  ownerHandle: string | null;
  /** Absolute preview URL — the origin the canonical link is built on. */
  url: string;
}

type Load =
  | { status: 'loading' }
  | { status: 'ready'; share: ShareState }
  | { status: 'error' };

interface ServerApp {
  url?: string;
  isPublic?: boolean;
  slug?: string | null;
  ownerHandle?: string | null;
}

export function PublishControls({ appId }: { appId: string }): JSX.Element {
  const [load, setLoad] = useState<Load>({ status: 'loading' });
  const [busy, setBusy] = useState(false);
  // A mutation failure message (a t('pub.*') string). Cleared on the next attempt.
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef<number | null>(null);

  function loadShareState(): void {
    setLoad({ status: 'loading' });
    let cancelled = false;
    fetch(`/v1/creator/apps/${encodeURIComponent(appId)}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((j: { app?: ServerApp }) => {
        if (cancelled) return;
        const app = j.app;
        if (!app) {
          setLoad({ status: 'error' });
          return;
        }
        setLoad({
          status: 'ready',
          share: {
            isPublic: app.isPublic === true,
            slug: app.slug ?? null,
            ownerHandle: app.ownerHandle ?? null,
            url: app.url || `/preview/${appId}/`
          }
        });
      })
      .catch(() => {
        if (!cancelled) setLoad({ status: 'error' });
      });
    loadShareState.cancel = () => {
      cancelled = true;
    };
  }
  loadShareState.cancel = (): void => {};

  useEffect(() => {
    loadShareState();
    return () => loadShareState.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId]);

  /** Turn the share link on — mints the slug/ownerHandle identity. */
  function publish(): Promise<void> {
    return mutateShare({ deviceId: getDeviceId(), public: true });
  }

  /** Turn the share link off — the app stays reachable at its own address, but
   *  its /r/ deep link stops resolving (the resolver 404s non-public apps). */
  function unpublish(): Promise<void> {
    return mutateShare({ deviceId: getDeviceId(), public: false });
  }

  /** Flip visibility via the share route. The response carries the minted
   *  slug + ownerHandle, which become the canonical identity we render. */
  async function mutateShare(body: { deviceId: string; public: boolean }): Promise<void> {
    if (busy || load.status !== 'ready') return;
    const next = body.public;
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`/v1/apps/${encodeURIComponent(appId)}/share`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body)
      });
      if (!res.ok) {
        setError(next ? t('pub.errPublish') : t('pub.errUnpublish'));
        return;
      }
      const j = (await res.json()) as { slug?: string; ownerHandle?: string; public?: boolean };
      setLoad((cur) =>
        cur.status === 'ready'
          ? {
              status: 'ready',
              share: {
                ...cur.share,
                isPublic: j.public === true,
                slug: j.slug ?? cur.share.slug,
                ownerHandle: j.ownerHandle ?? cur.share.ownerHandle
              }
            }
          : cur
      );
      window.posthog?.capture(next ? 'share_link_enabled' : 'share_link_disabled', { appId });
    } catch {
      setError(next ? t('pub.errPublish') : t('pub.errUnpublish'));
    } finally {
      setBusy(false);
    }
  }

  async function copyLink(link: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      // Older Safari without the async clipboard API — selection fallback.
      try {
        const el = document.createElement('textarea');
        el.value = link;
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      } catch {
        return; // clipboard truly unavailable — leave the visible URL selectable
      }
    }
    setCopied(true);
    if (copyTimer.current) window.clearTimeout(copyTimer.current);
    copyTimer.current = window.setTimeout(() => setCopied(false), 1600);
  }

  // ── load states ────────────────────────────────────────────────────────────
  if (load.status === 'loading') {
    return (
      <section className="surface flex items-center gap-3 p-4" data-publish-controls>
        <span className="skeleton-shimmer" style={{ width: 36, height: 36, borderRadius: 12, flexShrink: 0 }} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span className="skeleton-line skeleton-shimmer" style={{ width: '46%', height: 11 }} />
          <span className="skeleton-line skeleton-shimmer" style={{ width: '72%', height: 9 }} />
        </div>
      </section>
    );
  }

  if (load.status === 'error') {
    return (
      <section className="surface flex items-center gap-3 p-4" data-publish-controls>
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-semibold text-[color:var(--fg)]">{t('pub.title')}</p>
          <p className="text-[12px] leading-relaxed text-[color:var(--fg-muted)]">{t('pub.errLoad')}</p>
        </div>
        <button type="button" onClick={loadShareState} className="btn btn-secondary shrink-0">
          <RotateCw size={14} /> {t('pub.retry')}
        </button>
      </section>
    );
  }

  const { share } = load;
  const liveLink =
    share.isPublic && share.slug && share.ownerHandle
      ? canonicalShareUrl({ slug: share.slug, ownerHandle: share.ownerHandle, url: share.url, appId })
      : null;

  return (
    <section className="surface flex flex-col gap-3 p-4" data-publish-controls>
      <div className="flex items-center gap-3">
        <span
          className="grid h-9 w-9 shrink-0 place-items-center rounded-xl"
          style={{
            background: share.isPublic ? 'var(--color-primary)' : 'color-mix(in srgb, var(--fg) 10%, transparent)',
            color: share.isPublic ? '#fff' : 'var(--fg-muted)'
          }}
        >
          {share.isPublic ? <Globe size={16} aria-hidden="true" /> : <Lock size={16} aria-hidden="true" />}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-semibold text-[color:var(--fg)]">
            {share.isPublic ? t('pub.liveTitle') : t('pub.privateTitle')}
          </p>
          <p className="text-[12px] leading-relaxed text-[color:var(--fg-muted)]">
            {share.isPublic ? t('pub.liveSub') : t('pub.privateSub')}
          </p>
        </div>
        {share.isPublic ? (
          <button
            type="button"
            onClick={() => void unpublish()}
            disabled={busy}
            className="btn btn-secondary shrink-0"
          >
            {busy ? t('pub.unpublishing') : t('pub.unpublish')}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => void publish()}
            disabled={busy}
            className="btn btn-primary shrink-0"
          >
            {busy ? t('pub.publishing') : t('pub.publish')}
          </button>
        )}
      </div>

      {/* The canonical identity — shown only while it actually resolves
          (the /r/ resolver 404s for private apps, on purpose). */}
      {liveLink && (
        <div className="flex items-center gap-2">
          <span className="inline-flex shrink-0 items-center gap-1 text-[12px] font-semibold text-[color:var(--color-success)]">
            <Check size={13} aria-hidden="true" /> {t('pub.liveBadge')}
          </span>
          <button
            type="button"
            onClick={() => void copyLink(liveLink)}
            className="group flex min-w-0 flex-1 items-center gap-2 rounded-xl border border-[color:var(--border)] bg-[color:var(--bg-subtle)] px-3 py-2 text-left transition-colors hover:bg-[color:var(--bg-elevated)]"
            aria-label={t('pub.ariaCopy')}
          >
            <span className="truncate text-[12px] font-mono text-[color:var(--fg-muted)]">{liveLink}</span>
            <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-medium text-[color:var(--fg-subtle)] group-hover:text-[color:var(--fg)]">
              {copied ? <Check size={12} /> : <Copy size={12} />}
              {copied ? t('pub.copied') : t('pub.copy')}
            </span>
          </button>
        </div>
      )}

      {error && (
        <p className="text-[12px] leading-relaxed" style={{ color: 'var(--color-danger)' }} role="alert">
          {error}
        </p>
      )}
    </section>
  );
}
